import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LiaCommentDots } from "react-icons/lia";
import { BiLaptop, BiMobile } from "react-icons/bi";
import CommentBox from "../../components/Common/CommentBox";
import { commentService } from "../../services/commentService";
import { useAuth } from "../../components/Context/authContext";
import { getContentPostById } from "../../services/contentPostService";
import EmailTemplatePreview from "../../components/Email/EmailTemplatePreview";
import { EmailSectionProvider } from "../../components/Context/EmailSectionContext";
import "./FreeFormContent.css";
import "../Blog/BlogPreview.css";

/**
 * Unified Preview Component for DIY Agents
 * Handles Blog, Freeform, and Email content types
 */
const DiyAgentPreview = () => {
  const { postId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  
  // Get content from location state or fetch by ID
  const contentData = location.state?.contentData;
  const contentType = location.state?.contentType || 'freeform'; // 'blog', 'freeform', 'email'
  const viewModeFromState = location.state?.viewMode;
  
  const [content, setContent] = useState(contentData);
  const [loading, setLoading] = useState(!contentData);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(viewModeFromState || "desktop");
  
  // Comment system state
  const previewRef = useRef(null);
  const [mouseY, setMouseY] = useState(window.innerHeight / 2);
  const [insidePreview, setInsidePreview] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [comments, setComments] = useState([]);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentPosition, setCommentPosition] = useState(null);
  
  // Email-specific state
  const [emailSections, setEmailSections] = useState([]);
  const [styleSettings, setStyleSettings] = useState({
    primaryColor: "#333333",
    secondaryColor: "#a1894c",
    backgroundColor: "#ffffff",
    fontFamily: "helvetica, 'helvetica neue', arial, verdana, sans-serif"
  });
  const [templateImages, setTemplateImages] = useState([]);

  // Load content by ID if not provided in state
  useEffect(() => {
    const loadContent = async () => {
      console.log('DiyAgentPreview - Loading content:', { postId, hasContentData: !!contentData });
      
      // Priority: If we have a valid postId, always fetch from API to get latest data
      if (postId && postId !== 'new' && postId !== 'undefined') {
        try {
          setLoading(true);
          setError(null);
          console.log('Fetching content post with ID:', postId);
          const response = await getContentPostById(postId);
          console.log('API Response:', response);
          
          if (response && response.content_post) {
            const post = response.content_post;
            setContent(post);
            
            // Parse generated_content to determine content type
            if (post.generated_content) {
              try {
                const parsed = typeof post.generated_content === 'string' 
                  ? JSON.parse(post.generated_content) 
                  : post.generated_content;
                
                // Detect content type
                if (parsed.blog_response || parsed.blog_response?.sections) {
                  // Will be handled as blog
                } else if (parsed.email_response || parsed.email_sections) {
                  // Process email sections and attachments
                  let emailSectionsToProcess = [];
                  if (parsed.email_response && Array.isArray(parsed.email_response)) {
                    emailSectionsToProcess = parsed.email_response;
                  } else if (parsed.email_sections) {
                    emailSectionsToProcess = Array.isArray(parsed.email_sections) 
                      ? parsed.email_sections 
                      : Object.values(parsed.email_sections);
                  }
                  
                  // Process attachments
                  if (post.attachments && Array.isArray(post.attachments) && post.attachments.length > 0) {
                    const metafields = post.metafields ? (typeof post.metafields === 'string' ? JSON.parse(post.metafields) : post.metafields) : {};
                    const imageSectionNames = metafields.image_section_names || [];
                    
                    const attachmentMap = {};
                    imageSectionNames.forEach((sectionName, index) => {
                      if (post.attachments[index] && post.attachments[index].url) {
                        attachmentMap[sectionName] = post.attachments[index].url;
                      }
                    });
                    
                    // Apply attachments to sections
                    const processedSections = emailSectionsToProcess.map((section, index) => {
                      const newSection = { ...section };
                      const sectionId = section.id || `email-section-${index}-${section.component_name || 'text'}`;
                      
                      if (attachmentMap[sectionId]) {
                        newSection.image = attachmentMap[sectionId];
                        if (!newSection.image_placeholder) {
                          newSection.image_placeholder = attachmentMap[sectionId];
                        }
                      }
                      
                      ['image1', 'image2', 'image3', 'image4'].forEach(imgKey => {
                        const attachmentKey = `${sectionId}_${imgKey}`;
                        if (attachmentMap[attachmentKey]) {
                          newSection[imgKey] = attachmentMap[attachmentKey];
                        }
                      });
                      
                      if (section.features && Array.isArray(section.features)) {
                        newSection.features = section.features.map((feature, featureIndex) => {
                          const newFeature = { ...feature };
                          const featureImageKey = `${sectionId}_feature_${featureIndex}_image`;
                          if (attachmentMap[featureImageKey]) {
                            newFeature.image = attachmentMap[featureImageKey];
                            newFeature.image_placeholder = attachmentMap[featureImageKey];
                          }
                          return newFeature;
                        });
                      }
                      
                      newSection.id = sectionId;
                      return newSection;
                    });
                    
                    setEmailSections(processedSections);
                    setTemplateImages(imageSectionNames.map((name, idx) => ({
                      component_name: name,
                      link: post.attachments[idx]?.url
                    })).filter(img => img.link));
                  } else {
                    setEmailSections(emailSectionsToProcess);
                  }
                }
              } catch (e) {
                console.error('Error parsing content:', e);
              }
            }
            
            if (post.id) {
              loadComments();
            }
            setLoading(false);
          } else {
            console.warn('API returned no content_post');
            setError('Content not found');
            setLoading(false);
          }
        } catch (err) {
          console.error('Error loading content from API:', err);
          // Fallback to contentData if API fails
          if (contentData) {
            console.log('Using contentData as fallback');
            setContent(contentData);
            setLoading(false);
            if (contentData.id) {
              loadComments();
            }
          } else {
            setError('Failed to load content: ' + (err.response?.data?.message || err.message || 'Unknown error'));
            setLoading(false);
          }
        }
      } else if (contentData) {
        // No valid postId, but we have contentData - use it directly
        console.log('Using contentData directly (no postId)');
        setContent(contentData);
        setLoading(false);
        if (contentData.id) {
          loadComments();
        }
      } else {
        // No postId and no contentData
        console.warn('No postId and no contentData');
        setError('No content to display. Please generate content first.');
        setLoading(false);
      }
    };

    loadContent();
  }, [postId, contentData]);

  // Load comments from backend
  const loadComments = async () => {
    const id = postId || content?.id;
    if (!id) return;
    
    try {
      const response = await commentService.getComments('ContentPost', id);
      const formattedComments = response.data.map(comment => ({
        id: comment.id,
        text: comment.content,
        position: comment.position_y,
        isSaved: true,
        user: comment.user,
        createdAt: comment.formatted_created_at
      }));
      setComments(formattedComments);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  // Comment system handlers
  const handleCommentClick = () => {
    setCommentPosition(mouseY);
    setShowCommentBox(true);
  };

  const handleSaveComment = async (text) => {
    const id = postId || content?.id;
    if (!text.trim() || !id) return;
    try {
      const commentData = {
        content: text.trim(),
        position_x: 0,
        position_y: commentPosition,
      };
      const response = await commentService.createComment('ContentPost', id, commentData);
      const created = response.data;
      setComments((prev) => [
        ...prev,
        {
          id: created.id,
          text: created.content,
          position: created.position_y,
          isSaved: true,
          user: created.user,
          createdAt: created.formatted_created_at
        },
      ]);
      setShowCommentBox(false);
    } catch (err) {
      console.error('Failed to save comment', err);
      toast.error('Failed to save comment. Please try again.');
    }
  };

  const handleEditComment = async (index, newText) => {
    const comment = comments[index];
    const id = postId || content?.id;
    if (!comment.id || !id) {
      const updatedComments = [...comments];
      updatedComments[index].text = newText;
      setComments(updatedComments);
      return;
    }
    
    try {
      const commentData = { content: newText.trim() };
      const response = await commentService.updateComment('ContentPost', id, comment.id, commentData);
      const updatedComments = [...comments];
      updatedComments[index] = {
        ...updatedComments[index],
        text: response.data.content,
        createdAt: response.data.formatted_created_at
      };
      setComments(updatedComments);
      toast.success('Comment updated successfully!');
    } catch (error) {
      console.error('Failed to update comment:', error);
      toast.error('Failed to update comment. Please try again.');
    }
  };

  const handleDeleteComment = async (index) => {
    const comment = comments[index];
    const id = postId || content?.id;
    if (!comment.id || !id) {
      setComments(comments.filter((_, i) => i !== index));
      return;
    }
    
    try {
      await commentService.deleteComment('ContentPost', id, comment.id);
      setComments(comments.filter((_, i) => i !== index));
      toast.success('Comment deleted successfully!');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment. Please try again.');
    }
  };

  // Mouse tracking for comment positioning
  useEffect(() => {
    const handleMouseMove = (e) => {
      const previewContainer = previewRef.current;
      if (previewContainer) {
        const { top, height } = previewContainer.getBoundingClientRect();
        let relativeY = e.clientY - top;
        let clampedY = Math.max(50, Math.min(relativeY, height - 50));
        setMouseY(clampedY);
      }
    };

    const handleMouseEnter = () => setInsidePreview(true);
    const handleMouseLeave = () => setInsidePreview(false);

    document.addEventListener("mousemove", handleMouseMove);
    const previewContainer = previewRef.current;
    if (previewContainer) {
      previewContainer.addEventListener("mouseenter", handleMouseEnter);
      previewContainer.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (previewContainer) {
        previewContainer.removeEventListener("mouseenter", handleMouseEnter);
        previewContainer.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  // Determine content type from content data
  const detectContentType = () => {
    if (contentType && ['blog', 'freeform', 'email'].includes(contentType)) {
      return contentType;
    }
    
    if (!content?.generated_content) return 'freeform';
    
    try {
      const parsed = typeof content.generated_content === 'string' 
        ? JSON.parse(content.generated_content) 
        : content.generated_content;
      
      if (parsed.blog_response || parsed.blog_response?.sections) {
        return 'blog';
      } else if (parsed.email_response || parsed.email_sections) {
        return 'email';
      }
    } catch (e) {
      // If parsing fails, treat as freeform
    }
    
    return 'freeform';
  };

  const finalContentType = detectContentType();

  // Blog rendering functions (from BlogPreview.jsx)
  const renderFAQContent = (content) => {
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const faqItems = contentString.split(/(?=\d+\.\s*\*\*)/);

    return (
      <div style={{ marginBottom: 16 }}>
        {faqItems.map((item, index) => {
          if (!item.trim()) return null;
          const match = item.match(/(\d+)\.\s*\*\*(.*?\?)\*\*\s*(.*)/s);
          if (match) {
            const [, number, question, answer] = match;
            return (
              <div
                key={index}
                style={{
                  marginBottom: 20,
                  backgroundColor: "#f8fafc",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 15 : 17,
                    color: "#E36250",
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#E36250",
                      color: "white",
                      borderRadius: "50%",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      marginRight: "12px",
                      flexShrink: 0,
                    }}
                  >
                    {number}
                  </span>
                  {question}
                </div>
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 13 : 15,
                    color: "#4b5563",
                    paddingLeft: 36,
                    lineHeight: 1.6,
                  }}
                >
                  {answer.trim()}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const renderFAQObjectContent = (content) => {
    return (
      <div style={{ marginBottom: 16 }}>
        {content.map((faq, index) => (
          <div
            key={index}
            style={{
              marginBottom: 20,
              backgroundColor: "#f8fafc",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: viewMode === "mobile" ? 15 : 17,
                color: "#E36250",
                marginBottom: 12,
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <span
                style={{
                  backgroundColor: "#E36250",
                  color: "white",
                  borderRadius: "50%",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  marginRight: "12px",
                  flexShrink: 0,
                }}
              >
                Q
              </span>
              {faq.question}
            </div>
            <div
              style={{
                fontSize: viewMode === "mobile" ? 13 : 15,
                color: "#4b5563",
                paddingLeft: 40,
                lineHeight: 1.6,
              }}
            >
              {faq.answer}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMythsContent = (content) => {
    if (typeof content === 'string') {
      const myths = content.split(/(?=\d+\.\s*\*\*)/);
      return (
        <div style={{ marginBottom: 16 }}>
          {myths.map((myth, index) => {
            if (!myth.trim()) return null;
            const match = myth.match(/(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
            if (match) {
              const [, number, question, answer] = match;
              return (
                <div key={index} style={{ 
                  marginBottom: 16, 
                  padding: '16px', 
                  backgroundColor: '#fef3f2', 
                  borderRadius: '8px', 
                  border: '1px solid #fecaca', 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
                }}>
                  <div style={{ 
                    fontWeight: 600, 
                    fontSize: viewMode === "mobile" ? 15 : 17, 
                    color: '#dc2626', 
                    marginBottom: '8px', 
                    display: 'flex', 
                    alignItems: 'center' 
                  }}>
                    <span style={{ 
                      backgroundColor: '#dc2626', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: '24px', 
                      height: '24px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '12px', 
                      marginRight: '12px',
                      flexShrink: 0
                    }}>
                      {number}
                    </span>
                    {question}
                  </div>
                  <div style={{ 
                    fontSize: viewMode === "mobile" ? 13 : 15, 
                    color: '#4b5563', 
                    lineHeight: 1.6, 
                    paddingLeft: '36px' 
                  }}>
                    {answer.trim()}
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }
    return null;
  };

  // Helper function to render blog section content (takes content as parameter)
  const renderBlogSectionContent = (content) => {
    if (!content) return null;
    if (content === null || content === undefined) return null;

    if (Array.isArray(content) && content.length > 0) {
      const isFAQFormat = content.slice(0, 3).every(item => 
        item && typeof item === 'object' && item.question && item.answer
      );
      if (isFAQFormat) {
        return renderFAQObjectContent(content);
      }
      
      if (content.every(item => typeof item === 'string')) {
        return renderArrayContent(content);
      }
      
      return renderMixedArrayContent(content);
    }

    if (typeof content === 'object' && content !== null && !Array.isArray(content)) {
      return renderObjectContent(content);
    }

    if (typeof content === 'string' && content.includes('**') && content.match(/\d+\.\s*\*\*.*?\*\*:\s*/)) {
      return renderMythsContent(content);
    }

    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }

    const hasTable = contentString.split("\n").filter((line) => line.includes("|")).length >= 2;
    if (hasTable) {
      return renderTableContent(contentString);
    }

    if (contentString.includes("**") && contentString.match(/\d+\.\s*\*\*.*?\?\*\*/)) {
      return renderFAQContent(contentString);
    }

    if (contentString.includes("Q:") && contentString.includes("A:")) {
      return renderTraditionalQAContent(contentString);
    }

    if (contentString.match(/^\d+\.\s*\*\*.*?\*\*/m)) {
      return renderNumberedListContent(contentString);
    }

    if (contentString.match(/^-\s*\*\*.*?\*\*/m)) {
      return renderBulletListContent(contentString);
    }

    return renderParagraphContent(contentString);
  };

  const renderArrayContent = (contentArray) => {
    const isFAQArray = contentArray.length > 0 && 
      contentArray.every(item => 
        typeof item === 'string' && 
        item.match(/^\d+\.\s*\*\*.*?\*\*\s*/)
      );
    
    if (isFAQArray) {
      return (
        <div style={{ marginBottom: 16, textAlign: 'left' }}>
          {contentArray.map((item, index) => {
            const faqMatch = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*\s*(.*)/);
            if (faqMatch) {
              const [, number, question, answer] = faqMatch;
              return (
                <div key={index} style={{ 
                  marginBottom: '20px', 
                  padding: '20px', 
                  backgroundColor: '#f8fafc', 
                  borderRadius: '12px', 
                  border: '1px solid #e2e8f0', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  textAlign: 'left'
                }}>
                  <div style={{ 
                    fontWeight: 600, 
                    fontSize: viewMode === "mobile" ? 14 : 16, 
                    color: '#E36250', 
                    marginBottom: '12px', 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    textAlign: 'left'
                  }}>
                    <span style={{ 
                      backgroundColor: '#E36250', 
                      color: 'white', 
                      borderRadius: '50%', 
                      width: '28px', 
                      height: '28px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '14px', 
                      marginRight: '12px',
                      flexShrink: 0
                    }}>
                      {number}
                    </span>
                    {question}
                  </div>
                  {answer && (
                    <div style={{ 
                      fontSize: viewMode === "mobile" ? 13 : 15, 
                      color: '#4b5563', 
                      lineHeight: '1.6', 
                      paddingLeft: '40px',
                      textAlign: 'left'
                    }}>
                      {answer}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={index} style={{ marginBottom: 12 }}>
                <p style={{
                  fontSize: viewMode === "mobile" ? 14 : 16,
                  color: "#374151",
                  lineHeight: 1.6,
                  marginBottom: 8,
                  textAlign: 'left',
                }}>
                  {item.trim()}
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    
    return (
      <div style={{ marginBottom: 16, textAlign: 'left' }}>
        {contentArray.map((item, index) => (
          <div key={index} style={{ marginBottom: 12 }}>
            {typeof item === 'string' ? (
              <p style={{
                fontSize: viewMode === "mobile" ? 14 : 16,
                color: "#374151",
                lineHeight: 1.6,
                marginBottom: 8,
                textAlign: 'left',
              }}>
                {item.trim()}
              </p>
            ) : (
              <div style={{
                fontSize: viewMode === "mobile" ? 14 : 16,
                color: "#6b7280",
                fontStyle: "italic",
                padding: "8px",
                backgroundColor: "#f9fafb",
                borderRadius: "4px",
                border: "1px solid #e5e7eb",
              }}>
                {JSON.stringify(item)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderMixedArrayContent = (contentArray) => {
    return (
      <div style={{ marginBottom: 16, textAlign: 'left' }}>
        {contentArray.map((item, index) => (
          <div key={index} style={{ marginBottom: 16 }}>
            {renderBlogSectionContent(item)}
          </div>
        ))}
      </div>
    );
  };

  const renderObjectContent = (contentObject) => {
    const entries = Object.entries(contentObject);
    
    return (
      <div style={{ marginBottom: 16, textAlign: 'left' }}>
        {entries.map(([key, value], index) => (
          <div key={index} style={{ 
            marginBottom: 16, 
            padding: '16px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px', 
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              fontWeight: 600,
              fontSize: viewMode === "mobile" ? 14 : 16,
              color: "#1f2937",
              marginBottom: 8,
              textTransform: "capitalize",
            }}>
              {key.replace(/_/g, ' ')}
            </div>
            <div>
              {typeof value === 'string' ? (
                <p style={{
                  fontSize: viewMode === "mobile" ? 13 : 15,
                  color: "#4b5563",
                  lineHeight: 1.6,
                  margin: 0,
                  textAlign: 'left',
                }}>
                  {value}
                </p>
              ) : Array.isArray(value) ? (
                renderArrayContent(value)
              ) : typeof value === 'object' ? (
                <div style={{
                  fontSize: viewMode === "mobile" ? 12 : 14,
                  color: "#6b7280",
                  fontFamily: "monospace",
                  backgroundColor: "#f3f4f6",
                  padding: "8px",
                  borderRadius: "4px",
                  overflow: "auto",
                }}>
                  {JSON.stringify(value, null, 2)}
                </div>
              ) : (
                <span style={{
                  fontSize: viewMode === "mobile" ? 13 : 15,
                  color: "#4b5563",
                }}>
                  {String(value)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTableContent = (content) => {
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const lines = contentString.split("\n").filter((line) => line.trim());
    const tableLines = lines.filter((line) => line.includes("|"));

    if (tableLines.length === 0) return renderParagraphContent(content);

    const headerLine = tableLines.find(
      (line) => line.includes("|") && !line.match(/^\s*\|[\s\-\|]+\|\s*$/)
    );

    if (!headerLine) return renderParagraphContent(contentString);

    const headers = headerLine
      .split("|")
      .map((h) => h.trim())
      .filter((h) => h);

    const separatorLineIndex = tableLines.findIndex((line) =>
      line.match(/^\s*\|[\s\-\|]+\|\s*$/)
    );

    const dataRows = tableLines.filter(
      (line, index) =>
        line !== headerLine &&
        !line.match(/^\s*\|[\s\-\|]+\|\s*$/) &&
        line.includes("|") &&
        line.split("|").filter((cell) => cell.trim()).length >= 2
    );

    const nonTableContent = [];
    let inTable = false;

    for (const line of lines) {
      if (line.includes("|")) {
        inTable = true;
      } else if (inTable && line.trim()) {
        nonTableContent.push({ text: line, position: "after" });
      } else if (!inTable && line.trim()) {
        nonTableContent.push({ text: line, position: "before" });
      }
    }

    const beforeTableContent = nonTableContent.filter(
      (item) => item.position === "before"
    );
    const afterTableContent = nonTableContent.filter(
      (item) => item.position === "after"
    );

    return (
      <div style={{ marginBottom: 16 }}>
        {beforeTableContent.map((item, index) => {
          const line = item.text;
          if (
            line.includes("**") &&
            (line.includes("Comparative") || line.includes("Analysis"))
          ) {
            return (
              <h3
                key={`before-${index}`}
                style={{
                  fontSize: viewMode === "mobile" ? 16 : 18,
                  color: "#1f2937",
                  fontWeight: 600,
                  marginBottom: 16,
                  marginTop: 8,
                }}
              >
                {line.replace(/\*\*/g, "")}
              </h3>
            );
          }
          return (
            <p
              key={`before-${index}`}
              style={{
                fontSize: viewMode === "mobile" ? 14 : 16,
                color: "#374151",
                lineHeight: 1.6,
                marginBottom: 12,
              }}
            >
              {line}
            </p>
          );
        })}

        <div
          style={{
            overflowX: "auto",
            marginBottom: 16,
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            backgroundColor: "#f9fafb",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: viewMode === "mobile" ? 12 : 14,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#E36250", color: "white" }}>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    style={{
                      padding: viewMode === "mobile" ? "8px" : "12px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: viewMode === "mobile" ? 12 : 14,
                    }}
                  >
                    {header.replace(/\*\*/g, "")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, rowIndex) => {
                const cells = row
                  .split("|")
                  .map((cell) => cell.trim())
                  .filter((cell) => cell);

                if (cells.length < 2) return null;

                return (
                  <tr
                    key={rowIndex}
                    style={{
                      backgroundColor:
                        rowIndex % 2 === 0 ? "#ffffff" : "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    {cells.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        style={{
                          padding: viewMode === "mobile" ? "8px" : "12px",
                          verticalAlign: "top",
                          borderRight:
                            cellIndex < cells.length - 1
                              ? "1px solid #e5e7eb"
                              : "none",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: cellIndex === 0 ? 600 : 400,
                            color: cellIndex === 0 ? "#1f2937" : "#4b5563",
                          }}
                        >
                          {cell.replace(/\*\*/g, "")}
                        </span>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {afterTableContent.map((item, index) => (
          <p
            key={`after-${index}`}
            style={{
              fontSize: viewMode === "mobile" ? 14 : 16,
              color: "#374151",
              lineHeight: 1.6,
              marginBottom: 12,
            }}
          >
            {item.text}
          </p>
        ))}
      </div>
    );
  };

  const renderTraditionalQAContent = (content) => {
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const qaItems = contentString.split(/\n\s*\n/).filter((item) => item.trim());

    return (
      <div style={{ marginBottom: 16 }}>
        {qaItems.map((item, index) => {
          const lines = item.split("\n").filter((line) => line.trim());
          const question = lines.find((line) => line.startsWith("Q:"));
          const answer = lines.find((line) => line.startsWith("A:"));

          if (question && answer) {
            return (
              <div key={index} style={{ marginBottom: 20 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 14 : 16,
                    color: "#2563eb",
                    marginBottom: 4,
                  }}
                >
                  {question.replace("Q:", "").trim()}
                </div>
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 13 : 15,
                    color: "#444",
                    paddingLeft: 16,
                    borderLeft: "3px solid #e5e7eb",
                  }}
                >
                  {answer.replace("A:", "").trim()}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const renderNumberedListContent = (content) => {
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const items = contentString.split(/(?=\d+\.\s*\*\*)/);

    return (
      <div style={{ marginBottom: 16 }}>
        {items.map((item, index) => {
          if (!item.trim()) return null;

          const match = item.match(/^(\d+)\.\s*\*\*(.*?)\*\*:\s*(.*)/s);
          if (match) {
            const [, number, title, description] = match;
            return (
              <div key={index} style={{ 
                marginBottom: 16, 
                padding: '16px', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '8px', 
                border: '1px solid #bae6fd',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 15 : 17,
                    color: "#0369a1",
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ 
                    backgroundColor: '#0369a1', 
                    color: 'white', 
                    borderRadius: '50%', 
                    width: '24px', 
                    height: '24px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontSize: '12px', 
                    marginRight: '12px',
                    flexShrink: 0
                  }}>
                    {number}
                  </span>
                  {title}
                </div>
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 13 : 15,
                    color: "#4b5563",
                    paddingLeft: 36,
                    lineHeight: 1.6,
                  }}
                >
                  {description.trim()}
                </div>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const renderBulletListContent = (content) => {
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const lines = contentString.split("\n").filter((line) => line.trim());
    const items = [];
    let currentItem = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const bulletMatch = line.match(/^-\s*\*\*(.*?)\*\*:?\s*(.*)?$/);

      if (bulletMatch) {
        if (currentItem) {
          items.push(currentItem);
        }
        const [, title, initialDescription] = bulletMatch;
        currentItem = {
          title: title.trim(),
          description: initialDescription ? initialDescription.trim() : "",
        };
      } else if (currentItem && line.trim()) {
        if (currentItem.description) {
          currentItem.description += " " + line.trim();
        } else {
          currentItem.description = line.trim();
        }
      } else if (!line.match(/^-\s*\*\*/) && line.trim()) {
        if (!currentItem) {
          currentItem = {
            title: "",
            description: line.trim(),
          };
        }
      }
    }

    if (currentItem) {
      items.push(currentItem);
    }

    if (items.length === 0 || items.every((item) => !item.title)) {
      return renderParagraphContent(content);
    }

    return (
      <div style={{ marginBottom: 16 }}>
        {items.map((item, index) => {
          if (!item.title && !item.description) return null;

          return (
            <div key={index} style={{ marginBottom: 16 }}>
              {item.title && (
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 15 : 17,
                    color: "#dc2626",
                    marginBottom: 4,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ marginRight: 8 }}>•</span>
                  {item.title}
                </div>
              )}
              {item.description && (
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 13 : 15,
                    color: "#4b5563",
                    paddingLeft: item.title ? 20 : 0,
                    lineHeight: 1.6,
                  }}
                >
                  {item.description}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderParagraphContent = (content) => {
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    const paragraphs = contentString.split("\n").filter((p) => p.trim());

    return (
      <div style={{ marginBottom: 16, textAlign: 'left' }}>
        {paragraphs.map((paragraph, index) => (
          <p
            key={index}
            style={{
              fontSize: viewMode === "mobile" ? 14 : 16,
              color: "#374151",
              lineHeight: 1.6,
              marginBottom: 12,
              textAlign: 'left',
            }}
          >
            {paragraph.trim()}
          </p>
        ))}
      </div>
    );
  };

  // Render Blog Content (from BlogPreview.jsx)
  const renderBlogContent = () => {
    if (!content?.generated_content) return null;
    
    try {
      const parsed = typeof content.generated_content === 'string' 
        ? JSON.parse(content.generated_content) 
        : content.generated_content;
      
      const blogData = parsed.blog_response || parsed;
      const sections = blogData?.sections || [];
      const h1Title = blogData?.h1_title || content?.title || "";
      
      return (
        <div>
          {h1Title && (
            <h1 style={{
              fontSize: viewMode === "mobile" ? 24 : 32,
              fontWeight: 700,
              color: "#111827",
              marginBottom: 32,
              lineHeight: 1.2,
              textAlign: "left",
            }}>
              {h1Title}
            </h1>
          )}
          
          {sections.map((section, idx) => (
            <div
              key={idx}
              className="blog-section"
              style={{
                marginBottom: 40,
                borderBottom: idx < sections.length - 1 ? "1px solid #f3f4f6" : "none",
                paddingBottom: idx < sections.length - 1 ? 32 : 0,
              }}
            >
              {section.image && (
                <img
                  src={section.image}
                  alt={section.section_title || ""}
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    marginBottom: 16,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                />
              )}

              {section.section_title && (
                <h2
                  style={{
                    fontWeight: 600,
                    fontSize: viewMode === "mobile" ? 20 : 26,
                    marginBottom: 16,
                    color: "#1f2937",
                    borderLeft: "4px solid #E36250",
                    paddingLeft: 16,
                    lineHeight: 1.3,
                  }}
                >
                  {section.section_title}
                </h2>
              )}

              {section.content && (
                <div style={{ marginBottom: 16 }}>
                  {renderBlogSectionContent(section.content)}
                </div>
              )}

              {section.tags && (
                <div style={{ margin: "16px 0" }}>
                  {section.tags.map((tag, i) => (
                    <span
                      key={i}
                      style={{
                        background: "#dbeafe",
                        color: "#dc2626",
                        borderRadius: 16,
                        padding: "4px 12px",
                        marginRight: 8,
                        marginBottom: 4,
                        fontSize: viewMode === "mobile" ? 12 : 14,
                        fontWeight: 500,
                        display: "inline-block",
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {section.keywords_integrated && (
                <div
                  style={{
                    margin: "16px 0",
                    padding: "12px",
                    backgroundColor: "#f9fafb",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      fontSize: viewMode === "mobile" ? 12 : 14,
                      fontWeight: 600,
                      color: "#6b7280",
                      marginBottom: 8,
                    }}
                  >
                    Related Keywords:
                  </div>
                  <div>
                    {section.keywords_integrated.map((keyword, i) => (
                      <span
                        key={i}
                        style={{
                          background: "#f3f4f6",
                          color: "#374151",
                          borderRadius: 12,
                          padding: "2px 8px",
                          marginRight: 6,
                          marginBottom: 4,
                          fontSize: viewMode === "mobile" ? 11 : 13,
                          display: "inline-block",
                        }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {section.suggested_image_or_infographic && (
                <div
                  style={{
                    margin: "16px 0",
                    padding: "12px",
                    backgroundColor: "#fef3c7",
                    borderRadius: "8px",
                    border: "1px solid #fbbf24",
                  }}
                >
                  <div
                    style={{
                      fontSize: viewMode === "mobile" ? 12 : 14,
                      fontWeight: 600,
                      color: "#92400e",
                      marginBottom: 4,
                    }}
                  >
                    💡 Suggested Visual:
                  </div>
                  <div
                    style={{
                      fontSize: viewMode === "mobile" ? 12 : 14,
                      color: "#a16207",
                      fontStyle: "italic",
                    }}
                  >
                    {section.suggested_image_or_infographic}
                  </div>
                </div>
              )}

              {section.word_count && (
                <div
                  style={{
                    fontSize: viewMode === "mobile" ? 11 : 13,
                    color: "#9ca3af",
                    textAlign: "right",
                    marginTop: 8,
                  }}
                >
                  {section.word_count} words
                </div>
              )}
            </div>
          ))}
        </div>
      );
    } catch (e) {
      console.error('Error rendering blog content:', e);
      return <div>Error rendering blog content</div>;
    }
  };

  // Render Freeform Content (from PreviewFreeFormContent.jsx)
  const renderFreeformContent = () => {
    const generatedContent = content?.generated_content || "";
    
    if (!generatedContent) {
      return (
        <div style={{ 
          textAlign: 'center', 
          color: '#6b7280', 
          padding: '40px 20px' 
        }}>
          <p>No content generated yet.</p>
        </div>
      );
    }

    // Try to parse JSON and extract freeform_response.response
    let displayContent = '';
    try {
      const parsed = typeof generatedContent === 'string' 
        ? JSON.parse(generatedContent) 
        : generatedContent;
      
      // Extract freeform_response.response if available
      if (parsed.freeform_response && parsed.freeform_response.response) {
        displayContent = parsed.freeform_response.response;
      } else if (parsed.response && typeof parsed.response === 'string') {
        displayContent = parsed.response;
      } else if (parsed.content && typeof parsed.content === 'string') {
        displayContent = parsed.content;
      } else if (typeof parsed === 'string') {
        displayContent = parsed;
      } else {
        // If it's not a string, fall back to the original content
        displayContent = typeof generatedContent === 'string' ? generatedContent : JSON.stringify(generatedContent);
      }
    } catch (e) {
      // If parsing fails, use the content as-is
      displayContent = typeof generatedContent === 'string' ? generatedContent : JSON.stringify(generatedContent);
    }

    // Clean and format the content
    const cleanContent = (content) => {
      if (!content || typeof content !== 'string') return content || '';
      
      let cleaned = content
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\t/g, '\t')
        .replace(/\s+$/, '')
        .trim();
      
      if (/<[^>]+>/.test(cleaned)) {
        return cleaned;
      }
      
      if (cleaned && !cleaned.includes('<')) {
        return cleaned.split('\n\n').map(paragraph => 
          paragraph.trim() ? `<p>${paragraph.trim()}</p>` : ''
        ).join('');
      }
      
      return cleaned;
    };

    const cleanedContent = cleanContent(displayContent);

    return (
      <div 
        className="generated-html-content"
        dangerouslySetInnerHTML={{ __html: cleanedContent }}
        style={{ 
          maxWidth: '100%', 
          overflowX: 'auto', 
          margin: '0', 
          padding: '0' 
        }}
      />
    );
  };

  // Render Email Content (from PreviewEmail/index.jsx)
  const renderEmailContent = () => {
    if (emailSections.length === 0) {
      return (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <h4>No email content to preview</h4>
          <p>Add some sections to see the preview.</p>
        </div>
      );
    }

    return (
      <div style={{ 
        width: '100%', 
        maxWidth: '100%', 
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        <EmailSectionProvider initialSections={emailSections}>
          <EmailTemplatePreview
            styleSettings={styleSettings}
            sectionsData={emailSections}
            viewMode={viewMode}
            templateImages={templateImages}
          />
        </EmailSectionProvider>
      </div>
    );
  };

  // Main render function based on content type
  const renderContent = () => {
    switch (finalContentType) {
      case 'blog':
        return renderBlogContent();
      case 'email':
        return renderEmailContent();
      case 'freeform':
      default:
        return renderFreeformContent();
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div>Loading preview...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
        <h4>Error</h4>
        <p>{error}</p>
        <button onClick={() => navigate(-1)}>Back</button>
      </div>
    );
  }

  if (!content) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h4>No Content</h4>
        <p>No content data available for preview.</p>
        <button onClick={() => navigate(-1)}>Back</button>
      </div>
    );
  }

  return (
    <div className={`${finalContentType}-preview-main`} style={{ textAlign: 'left', width: '100%' }}>
      <ToastContainer />
      
      {/* Top Bar - Same as BlogPreview */}
      <div className="preview-header">
        <div
          className="preview-header-back"
          onClick={() => navigate(-1)}
        >
          &lt; Back
        </div>
        <div className="device-icons d-flex border border-black rounded justify-content-between align-items-center">
          <h6 className="mt-2 mx-1">Preview</h6>
          <div className="d-flex justify-content-center align-items-center">
            <button className="btn" onClick={() => setViewMode("desktop")}>
              <BiLaptop size={20} />
            </button> |
            <button className="btn" onClick={() => setViewMode("mobile")}>
              <BiMobile size={20} />
            </button>
          </div>
        </div>
        <div className="btns d-flex justify-content-end gap-3">
          <div>
            <button
              className="btn bgprimary rounded"
              onClick={() => {
                const shareUrl = postId 
                  ? `${window.location.origin}/skills/diy-agent/preview/${postId}`
                  : window.location.href;
                navigator.clipboard.writeText(shareUrl);
                toast.success("Link copied to clipboard!");
              }}
            >
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Content with responsive container */}
      <div 
        className="d-flex p-4" 
        style={{ 
          justifyContent: 'center', 
          textAlign: 'left' 
        }}
      >
        <div
          ref={previewRef}
          className={`content-preview-container ${viewMode}`}
          style={{
            width: viewMode === "mobile" ? "375px" : "100%",
            maxWidth: viewMode === "mobile" ? "375px" : "1000px",
            border: viewMode === "mobile" ? "12px solid #333" : "none",
            borderRadius: viewMode === "mobile" ? "30px" : "0",
            height: viewMode === "mobile" ? "667px" : "auto",
            overflow: viewMode === "mobile" ? "auto" : "visible",
            overflowX: viewMode === "mobile" ? "hidden" : "visible",
            backgroundColor: finalContentType === 'email' ? styleSettings.backgroundColor : "white",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            transition: "all 0.3s ease",
            padding: viewMode === "mobile" ? "10px" : "20px",
            position: "relative",
            textAlign: "left",
          }}
        >
          {/* Comment System */}
          {!showCommentBox && (
            <div
              style={{
                position: "absolute",
                right: "-60px",
                top: `${mouseY}px`,
                zIndex: 1000,
                cursor: "pointer",
                fontSize: "24px",
                color: isHovered ? "#D04734" : "#666",
                backgroundColor: isHovered ? "rgba(208, 71, 52, 0.1)" : "rgba(0, 0, 0, 0.1)",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isHovered ? "0 4px 12px rgba(208, 71, 52, 0.3)" : "0 2px 8px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease",
                animation: isHovered ? "heartbeat 1.5s ease-in-out infinite" : "none",
                border: isHovered ? "2px solid #D04734" : "1px solid rgba(0,0,0,0.1)",
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={(e) => {
                e.stopPropagation();
                handleCommentClick();
              }}
              aria-label="Add comment"
              title="Add comment"
            >
              {isHovered ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 45 44"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M43.18 21.976C43.18 33.5312 33.708 42.952 22.09 42.952C17.576 42.952 13.358 41.5536 9.88 39.1248L1 42.952L4.848 34.12C2.406 30.7344 1 26.5392 1 21.976C1 10.4208 10.472 1 22.09 1C33.708 1 43.18 10.4208 43.18 21.976Z"
                    stroke="#D04734"
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="15" cy="22" r="2" fill="#D04734" />
                  <circle cx="22" cy="22" r="2" fill="#D04734" />
                  <circle cx="29" cy="22" r="2" fill="#D04734" />
                </svg>
              ) : (
                <LiaCommentDots />
              )}
            </div>
          )}
          
          {/* Existing Comments */}
          {comments.map((comment, index) => (
            <CommentBox
              key={index}
              position={comment.position}
              commentText={comment.text}
              isSaved={comment.isSaved}
              onSave={(text) => handleEditComment(index, text)}
              onEdit={(newText) => handleEditComment(index, newText)}
              onDelete={() => handleDeleteComment(index)}
              user={comment.user}
              canModify={!!currentUser}
            />
          ))}

          {/* New Comment Box */}
          {showCommentBox && (
            <CommentBox
              position={commentPosition}
              onSave={handleSaveComment}
              onCancel={() => setShowCommentBox(false)}
              user={currentUser}
            />
          )}

          {/* Main Title (for blog and freeform) */}
          {finalContentType !== 'email' && content?.title && (
            <h1
              style={{
                fontSize: viewMode === "mobile" ? 24 : 32,
                fontWeight: 700,
                color: "#111827",
                marginBottom: 32,
                lineHeight: 1.2,
                textAlign: "left",
              }}
            >
              {content.title}
            </h1>
          )}

          {/* Render content based on type */}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default DiyAgentPreview;

